import SettingsSection from "../../components/SettingsSection";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo } from "react";
import styles from "./Options.module.scss";
import { graphql } from "../../../../__generated__/gql";
import { useMutation, useQuery } from "urql";
import TextInput from "../../../../ui/TextInput/TextInput";
import UnsavedChangesModal from "../../../../components/UnsavedChangesModal/UnsavedChangesModal";
import Loader from "../../../../ui/Loader/Loader";
import { useToast } from "../../../../ui/Toast/ToastContext";
import Header from "../../components/Header/Header";
import Label from "../../../../ui/Label/Label";
import Select from "../../../../ui/Select/Select";
import countryOptions from "../../../Keywords/CountryLanguageSelect/country-options";

const KeywordOptionSettingsQuery = graphql(/* GraphQL */ `
  query keywordOptionSettings {
    keywordSettings {
      defaultWebsiteUrl
      defaultGoogleLocation
    }
  }
`);

const UpdateKeywordOptionSettingsMutation = graphql(/* GraphQL */ `
  mutation updateKeywordOptionSettings($input: UpdateKeywordSettingsInput!) {
    updateKeywordSettings(input: $input) {
      defaultWebsiteUrl
      defaultGoogleLocation
    }
  }
`);

const schema = z.object({
  defaultWebsiteUrl: z.string(),
  defaultGoogleLocation: z.string(),
});

type KeywordOptionsFormData = z.infer<typeof schema>;

function KeywordOptionSettingsForm({
  defaultValues,
  onSubmit,
}: {
  defaultValues: KeywordOptionsFormData;
  onSubmit: (data: KeywordOptionsFormData) => Promise<void>;
}) {
  const { showToast } = useToast();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<KeywordOptionsFormData>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const onSubmitWrapper = useCallback(
    async (data: KeywordOptionsFormData) => {
      await onSubmit(data);

      reset(data);

      showToast({
        title: "Keyword settings saved",
        variant: "success",
      });
    },
    [onSubmit, reset, showToast],
  );

  return (
    <form onSubmit={handleSubmit(onSubmitWrapper)}>
      <UnsavedChangesModal isDirty={isDirty} />
      <Header
        title="Keyword Options"
        description="Manage your defaults for keywords"
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        onCancel={() => reset()}
      />
      <SettingsSection
        label="Default website"
        description="The website that is used in competitor searches. Typically your website."
      >
        <div className={styles.inputSection}>
          <Controller
            name="defaultWebsiteUrl"
            control={control}
            render={({ field }) => (
              <Label label="Default website URL">
                <TextInput {...field} />
              </Label>
            )}
          />
        </div>
      </SettingsSection>
      <SettingsSection
        label="Default google location"
        description="The location and language you would like to search for keyword in"
      >
        <div className={styles.inputSection}>
          <Controller
            name="defaultGoogleLocation"
            control={control}
            render={({ field }) => (
              <Label label="Default Google location">
                <Select {...field} data={countryOptions} withinPortal />
              </Label>
            )}
          />
        </div>
      </SettingsSection>
    </form>
  );
}

function Options() {
  const [keywordOptionSettingsResponse] = useQuery({
    query: KeywordOptionSettingsQuery,
    requestPolicy: "network-only",
  });
  const [, updateKeywordOptionSettings] = useMutation(
    UpdateKeywordOptionSettingsMutation,
  );

  const onSubmit = useCallback(
    async (data: KeywordOptionsFormData) => {
      await updateKeywordOptionSettings({
        input: {
          defaultWebsiteUrl: data.defaultWebsiteUrl,
          defaultGoogleLocation: data.defaultGoogleLocation,
        },
      });
    },
    [updateKeywordOptionSettings],
  );

  const defaultValues: KeywordOptionsFormData = useMemo(() => {
    if (!keywordOptionSettingsResponse.data?.keywordSettings) {
      return {
        defaultWebsiteUrl: "",
        defaultGoogleLocation: "us:en",
      };
    }

    const { defaultWebsiteUrl, defaultGoogleLocation } =
      keywordOptionSettingsResponse.data.keywordSettings;

    return {
      defaultWebsiteUrl: defaultWebsiteUrl || "",
      defaultGoogleLocation: defaultGoogleLocation || "us:en",
    };
  }, [keywordOptionSettingsResponse.data]);

  if (keywordOptionSettingsResponse.fetching) {
    return (
      <div
        style={{
          padding: "32px 0",
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <KeywordOptionSettingsForm
      defaultValues={defaultValues}
      onSubmit={onSubmit}
    />
  );
}

export default Options;
